import type { FC } from 'react';
import type { Currency } from 'src/types/listing';

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { formatMoney } from 'src/utils/money';
import { resolveDealChain } from 'src/utils/dealChain';
import { findOwnPurchase } from 'src/utils/ownPurchase';
import { deliveryInvoiceView } from 'src/utils/deliveryInvoice';

import { useBuyerChains, useDeliveryDeal, useDeliveryDeals, useListBuyerDirectPurchases } from 'src/queries';

import { Stack } from 'src/components/themed/Stack';
import { Button } from 'src/components/themed/Button';
import { InvoiceCard } from 'src/components/InvoiceCard';
import { Typography } from 'src/components/themed/Typography';

/**
 * "You bought this car" — the sold listing, seen by the person who bought it (UI part §4).
 *
 * Renders nothing at all when the viewer has no record of buying this listing, which is the normal
 * case: the caller then shows the ordinary "Sold" copy. So this is additive — a stranger's view of
 * a sold car is untouched.
 */

interface PurchasedByYouProps {
  listingId: string;
  listingCurrency?: Currency;
  /** Only queried when the listing is actually unavailable — a car on sale needs none of this. */
  enabled: boolean;
  /** What a stranger sees: the ordinary "Sold" explanation. Rendered *instead* of the receipt. */
  fallback: React.ReactNode;
}

export const PurchasedByYou: FC<PurchasedByYouProps> = ({ listingId, listingCurrency, enabled, fallback }) => {
  const navigate = useNavigate();
  const { data: deals } = useDeliveryDeals({ enabled });
  const { data: purchases } = useListBuyerDirectPurchases({ enabled });
  const { data: buyerChains } = useBuyerChains();

  const own = useMemo(
    () =>
      findOwnPurchase({
        listingId,
        deliveryDeals: deals?.items,
        directPurchases: purchases?.items,
      }),
    [listingId, deals, purchases]
  );

  // The receipt has to carry the transaction that paid for the car, and the funding hash lives in
  // the deal's timeline — which api keeps out of list projections deliberately. So the one matched
  // deal is read in full; `poll: false` because this is a receipt on a static page, not a live
  // order screen. The query key is the deal screen's own, so opening the order reuses the answer.
  const ownDealId = own?.kind === 'delivery' ? own.deal.id : '';
  const { data: ownDeal } = useDeliveryDeal(ownDealId, { enabled: !!ownDealId, poll: false });

  if (!own) {
    return <>{fallback}</>;
  }

  if (own.kind === 'delivery') {
    const chain = resolveDealChain({ chainKey: own.deal.chainKey, buyerChains, listingCurrency });
    const invoice = deliveryInvoiceView(ownDeal ?? own.deal, chain);

    return (
      <Stack gap={2} width="100%">
        <Typography color="white" variant="large" fontWeight="600">
          You bought this car
        </Typography>
        <InvoiceCard
          lines={invoice.lines}
          total={invoice.total}
          footnote={invoice.footnote}
          issuedAt={invoice.issuedAt}
          paymentTxHash={invoice.paymentTxHash}
          chain={chain}
          meta={[
            { label: 'Order', value: own.deal.orderRef },
            { label: 'City', value: own.deal.citySnapshot },
          ]}
        />
        <Button
          title="Go to my order"
          variant="accent"
          onClick={() => navigate(`/delivery/${own.deal.id}`)}
        />
      </Stack>
    );
  }

  const chain = resolveDealChain({ chainKey: own.purchase.chainKey, buyerChains, listingCurrency });
  const price = formatMoney(own.purchase.amount, own.purchase.tokenSymbol);

  return (
    <Stack gap={2} width="100%">
      <Typography color="white" variant="large" fontWeight="600">
        You bought this car
      </Typography>
      <InvoiceCard
        title="Receipt"
        // Buy Now takes no platform fee, and the invoice says so by having no line for one.
        lines={[{ label: 'Car price', value: price }]}
        total={{ label: 'Total', value: price }}
        issuedAt={own.purchase.verifiedAt}
        paymentTxHash={own.purchase.txHash}
        chain={chain}
      />
    </Stack>
  );
};
